/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import _ from 'lodash';

/**
 * Format environment name for display
 */
const formatEnvName = (envName, fallback) => {
  return envName ? `${_.capitalize(envName)} ` : fallback;
};

/**
 * Build success message language keys
 */
const buildDualSuccessMessage = (actionVerb, currentEnvName, targetEnvName, targetTenantTitle) => {
  const target = formatEnvName(targetEnvName, 'publishResponse.TARGET');
  return [
    actionVerb,
    'publishResponse.SUCCESSFULLY_TO',
    'publishResponse.CURRENT',
    targetTenantTitle && 'publishResponse.TENANT',
    'publishResponse.AND',
    target,
    'publishResponse.ENV',
    targetTenantTitle && `(${targetTenantTitle})`
  ].filter(Boolean);
};

/**
 * Build warning message language keys for partial success
 */
const buildPartialSuccessMessage = (
  actionVerb,
  failAction,
  currentEnvName,
  targetEnvName,
  errorMessage,
  targetTenantTitle
) => {
  const target = formatEnvName(targetEnvName, 'publishResponse.TARGET');
  const keys = [
    actionVerb,
    'publishResponse.TO',
    'publishResponse.CURRENT',
    targetTenantTitle && 'publishResponse.TENANT',
    'publishResponse.BUT_FAILED_TO',
    failAction,
    target,
    'publishResponse.ENV',
    targetTenantTitle && `(${targetTenantTitle})`
  ].filter(Boolean);

  return errorMessage ? { keys, errorMessage } : keys;
};

/**
 * Build target tenant copy message
 */
const buildTargetTenantCopyMessage = (targetTenantCopy, targetTenantTitle) => {
  if (targetTenantCopy === true) {
    return ['publishResponse.AND', 'publishResponse.COPIED_TO', targetTenantTitle];
  }
  return ['publishResponse.BUT_FAILED_TO', 'publishResponse.COPY_ON', targetTenantTitle];
};

/**
 * Parse publish response and generate appropriate notification message
 * @param {Object} data - Response data
 * @param {string} defaultSuccessMessage - Default success message (language key from en.js)
 * @param {string} successAction - Action type ('publish' or 'create')
 * @param {Object} options - Additional options (e.g., targetTenantTitle)
 * @returns {Object} - { message, langMessage, type }
 */
const parsePublishResponse = (data, defaultSuccessMessage, successAction = 'publish', options = {}) => {
  const message = '';
  let langMessage = '';
  let notificationType = 'success';
  const { targetTenantTitle } = options;

  if (!data.publishStatus) {
    langMessage = defaultSuccessMessage;
    return { message, langMessage, type: notificationType };
  }

  const { currentEnv, targetEnv, currentEnvName, targetEnvName, targetEnvErrorMessage, targetTenantCopy } =
    data.publishStatus;
  const actionVerbKey =
    successAction === 'create' ? 'publishResponse.NEW_VERSION_CREATED' : 'publishResponse.GRAPH_PUBLISHED';

  if (currentEnv === true && targetEnv === true) {
    langMessage = buildDualSuccessMessage(actionVerbKey, currentEnvName, targetEnvName, targetTenantTitle);
  } else if (currentEnv === true && targetEnv === false) {
    const failActionKey = successAction === 'create' ? 'publishResponse.CREATE_ON' : 'publishResponse.PUBLISH_TO';
    const result = buildPartialSuccessMessage(
      actionVerbKey,
      failActionKey,
      currentEnvName,
      targetEnvName,
      targetEnvErrorMessage,
      targetTenantTitle
    );

    if (result.keys) {
      langMessage = [...result.keys];
      if (targetEnvErrorMessage) {
        langMessage.push('publishResponse.REASON');
        langMessage.push(`: ${targetEnvErrorMessage}`);
      }
    } else {
      langMessage = result;
    }

    notificationType = 'warning';
  } else if (currentEnv === true && _.isNil(targetEnv)) {
    langMessage = defaultSuccessMessage;
  }

  if (!_.isNil(targetTenantCopy) && targetTenantTitle) {
    const tenantCopyMessage = buildTargetTenantCopyMessage(targetTenantCopy, targetTenantTitle);
    if (Array.isArray(langMessage)) {
      langMessage = [
        ...langMessage,
        'publishResponse.IN',
        'publishResponse.CURRENT',
        'publishResponse.TENANT',
        ...tenantCopyMessage
      ];
    } else {
      langMessage = [
        langMessage,
        'publishResponse.IN',
        'publishResponse.CURRENT',
        'publishResponse.TENANT',
        ...tenantCopyMessage
      ];
    }

    if (targetTenantCopy === false) {
      notificationType = 'warning';
    }
  }

  return { message, langMessage, type: notificationType };
};

export default parsePublishResponse;
